import {
  type Configuration,
  type RedirectRequest,
  LogLevel,
  PublicClientApplication
} from "@azure/msal-browser";
import {
  createRedirectLoginRequest,
  logRedirectUriConfig,
  logRedirectConfig
} from "@/utilities/msal-utils";
import { IDENTITY_PROVIDERS, type IdentityProvider } from "@/types/core";
import openidConfiguration from "@/assets/data/openid-configuration.json";
import { config } from "@/config/environment";

// Simple debug utility to avoid circular dependency
const debugUtility = {
  registerInstance: (instance: PublicClientApplication) => {
    if (config.debug.enabled) {
      console.log("🔧 MSAL instance registered for debugging:", instance);
    }
  },
  logConfig: () => {
    if (config.debug.enabled) {
      console.group("⚙️ MSAL Configuration");
      console.log("Client ID:", config.msal.clientId);
      console.log("Authority:", config.msal.authority);
      console.log("Authority Domain:", config.msal.authorityDomain);
      console.log("Redirect URI:", config.msal.redirectUri);
      console.log(
        "Post Logout Redirect URI:",
        config.msal.postLogoutRedirectUri
      );
      console.log("Cache Location:", config.msal.cacheLocation);
      console.log("Scopes:", config.msal.scopes);
      console.log("Token Scopes:", config.msal.tokenScopes);
      console.log("Environment:", config.app.env);
      console.log("Debug Enabled:", config.debug.enabled);
      console.groupEnd();
    }
  },
  clearInstances: () => {
    if (config.debug.enabled) {
      console.log("🧹 MSAL instances cleared");
    }
  }
};

// MSAL configuration
export const msalConfig: Configuration = {
  auth: {
    clientId: config.msal.clientId,
    authority: config.msal.authority,
    redirectUri: config.msal.redirectUri,
    postLogoutRedirectUri: config.msal.postLogoutRedirectUri,
    authorityMetadata: JSON.stringify(openidConfiguration),
    knownAuthorities: [config.msal.authorityDomain],
    cloudDiscoveryMetadata: undefined,
    clientCapabilities: ["CP1"]
  },
  cache: {
    cacheLocation: config.msal.cacheLocation,
    cacheRetentionDays: 1
  },
  system: {
    // PWA-specific configurations
    allowRedirectInIframe: false, // Prevent redirect in iframe for PWA

    // MSAL 5.x: windowHashTimeout/iframeHashTimeout replaced with popupBridgeTimeout/iframeBridgeTimeout
    ...(import.meta.env.DEV && {
      popupBridgeTimeout: 60000,
      iframeBridgeTimeout: 6000
    }),

    // Logger options
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            if (config.debug.enabled) {
              console.info(message);
            }
            return;
          case LogLevel.Verbose:
            if (config.debug.enabled) {
              console.debug(message);
            }
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
      logLevel:
        import.meta.env.PROD || config.app.env === "production"
          ? LogLevel.Error
          : config.debug.enabled
            ? LogLevel.Verbose
            : LogLevel.Error
    }
  }
};

// Singleton pattern for MSAL instance
let msalInstance: PublicClientApplication | null = null;

export const getMsalInstance = (): PublicClientApplication => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);

    // Register instance for debugging in development
    if (import.meta.env.DEV) {
      debugUtility.registerInstance(msalInstance);
      debugUtility.logConfig();
      logRedirectUriConfig();
      logRedirectConfig();
    }
  }

  return msalInstance;
};

// Cleanup function for development hot reload
export const cleanupMsalInstance = (): void => {
  if (msalInstance) {
    if (import.meta.env.DEV) {
      debugUtility.clearInstances();
    }
    msalInstance = null;
  }
};

// Get redirect login request for different identity providers
export const getRedirectLoginRequest = (
  email: string,
  provider: IdentityProvider
): RedirectRequest => {
  if (provider === IDENTITY_PROVIDERS.EMAIL) {
    return createRedirectLoginRequest(
      config.msal.scopes,
      config.msal.authority,
      {
        loginHint: email,
        extraQueryParameters: {
          username: email
        }
      }
    );
  }

  const trimmedEmail = email.trim();

  return createRedirectLoginRequest(
    config.msal.scopes,
    config.msal.authority,
    {
      ...(trimmedEmail ? { loginHint: trimmedEmail } : {}),
      domainHint: provider
    }
  );
};
